import { useParams, useLocation } from "wouter";
import { getPOById, getItemsByPOId, getValidationIssuesByPOId } from "@/lib/mockData";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  FileText, 
  Download,
  Printer
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { WorkflowVisualization } from "@/components/dashboard/WorkflowVisualization";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMemo } from "react";

const PODetail = () => {
  const  id:any  = useParams();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Get data from client-side mock data
  const poData = useMemo(() => {
    if (!id) return null;
    return getPOById(parseInt(id));
  }, [id]);

  const poItems = useMemo(() => {
    if (!id) return [];
    return getItemsByPOId(parseInt(id));
  }, [id]);

  const validationIssues = useMemo(() => {
    if (!id) return [];
    return getValidationIssuesByPOId(parseInt(id));
  }, [id]);

  const handleApprove = () => {
    toast({
      title: "PO Approved",
      description: "The purchase order has been approved and moved to inventory check",
      variant: "default",
    });
  };

  const handleReject = () => {
    toast({
      title: "PO Rejected", 
      description: "The purchase order has been rejected",
      variant: "default",
    });
  };

  const handleDownloadPO = () => {
    toast({
      title: "Downloading PO",
      description: "The purchase order is being prepared for download",
    });
  };

  const handlePrintPO = () => {
    toast({
      title: "Printing PO",
      description: "The purchase order is being sent to the printer",
    });
  };

  const handleBackToList = () => {
    setLocation("/");
  };

  // Use the data from mock functions
  const po = poData;
  const items = poItems;
  const issues = validationIssues;

  // If no PO found, show error
  if (!po) {
    return (
      <main className="p-4 sm:px-6 lg:px-8">
        <div className="text-center py-8">
          <h1 className="text-2xl font-semibold text-gray-900">Purchase Order Not Found</h1>
          <p className="text-gray-600 mt-2">The requested purchase order could not be found.</p>
          <Button onClick={handleBackToList} className="mt-4">
            Back to List
          </Button>
        </div>
      </main>
    );
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "Completed":
      case "Complete":
        return "success";
      case "Ready for Allocation":
      case "In Progress":
        return "info";
      case "Validation Pending":
      case "Draft":
        return "warning";
      case "Validation Failed":
        return "destructive";
      default:
        return "pending";
    }
  };

  const getSeverityBadgeVariant = (severity: string) => {
    switch (severity) {
      case "high":
        return "destructive";
      case "medium":
        return "warning";
      case "low":
        return "info";
      default:
        return "default";
    }
  };

  const isPendingValidation = po.status === "Validation Pending";
  const hasValidationErrors = issues.some(issue => issue.severity === "high");

  return (
    <main className="p-4 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleBackToList}
            className="mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-semibold text-gray-900 flex items-center">
              {po.poNumber}
              <Badge 
                variant={getStatusBadgeVariant(po.status)} 
                className="ml-3"
              >
                {po.status}
              </Badge>
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {po.brand} - {po.category} | Created: {po.createdAt.toDateString()} | Last Updated: {po.updatedAt.toDateString()}
            </p>
          </div>
        </div>
        <div className="flex space-x-2 mt-4 sm:mt-0">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleDownloadPO}
            className="flex items-center"
          >
            <Download className="h-4 w-4 mr-1" />
            Download
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handlePrintPO}
            className="flex items-center"
          >
            <Printer className="h-4 w-4 mr-1" />
            Print
          </Button>
          {isPendingValidation && (
            <>
              <Button 
                variant="outline" 
                size="sm"
                className="text-destructive border-destructive hover:bg-destructive hover:text-white"
                onClick={handleReject}
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="text-success border-success hover:bg-success hover:text-white"
                onClick={handleApprove}
                disabled={hasValidationErrors}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </Button>
            </>
          )}
        </div>
      </div>

      {/* PO Workflow Status */}
      <Card className="mb-6">
        <CardHeader className="border-b border-gray-200">
          <CardTitle className="text-lg font-medium text-gray-900">PO Workflow Status</CardTitle>
        </CardHeader>
        <CardContent className="px-4 py-5 sm:p-6">
          <div className="overflow-x-auto" style={{ minHeight: "220px" }}>
            <WorkflowVisualization
              currentStepId={3}
              progress={po.workflowProgress}
            />
          </div>
        </CardContent>
      </Card>

      {/* PO Details and Items */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* PO Details */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-gray-900">PO Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="divide-y divide-gray-200">
              <div className="py-3 grid grid-cols-3">
                <dt className="text-sm font-medium text-gray-500">Supplier</dt>
                <dd className="text-sm text-gray-900 col-span-2">{po.supplier}</dd>
              </div>
              <div className="py-3 grid grid-cols-3">
                <dt className="text-sm font-medium text-gray-500">Order Date</dt>
                <dd className="text-sm text-gray-900 col-span-2">{po.createdAt.toDateString()}</dd>
              </div>
              <div className="py-3 grid grid-cols-3">
                <dt className="text-sm font-medium text-gray-500">Expected Delivery</dt>
                <dd className="text-sm text-gray-900 col-span-2">{po.estimatedDelivery.toDateString()}</dd>
              </div>
              <div className="py-3 grid grid-cols-3">
                <dt className="text-sm font-medium text-gray-500">Total Items</dt>
                <dd className="text-sm text-gray-900 col-span-2">{items.length}</dd>
              </div>
              <div className="py-3 grid grid-cols-3">
                <dt className="text-sm font-medium text-gray-500">Total Value</dt>
                <dd className="text-sm text-gray-900 col-span-2">₹{po.totalAmount.toLocaleString()}</dd>
              </div>
              <div className="py-3 grid grid-cols-3">
                <dt className="text-sm font-medium text-gray-500">Location</dt>
                <dd className="text-sm text-gray-900 col-span-2">{po.location}</dd>
              </div>
              <div className="py-3 grid grid-cols-3">
                <dt className="text-sm font-medium text-gray-500">Delivery Address</dt>
                <dd className="text-sm text-gray-900 col-span-2">{po.deliveryAddress}</dd>
              </div>
              {po.notes && (
                <div className="py-3 grid grid-cols-3">
                  <dt className="text-sm font-medium text-gray-500">Notes</dt>
                  <dd className="text-sm text-gray-900 col-span-2">{po.notes}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        {/* PO Items and Validation */}
        <Card className="lg:col-span-2">
          <Tabs defaultValue="items">
            <CardHeader className="pb-0">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg font-medium text-gray-900">PO Contents</CardTitle>
                <TabsList>
                  <TabsTrigger value="items" className="flex items-center">
                    <FileText className="h-4 w-4 mr-2" />
                    Items
                  </TabsTrigger>
                  <TabsTrigger value="validation" className="flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Validation Issues
                    {issues.length > 0 && (
                      <Badge variant="destructive" className="ml-2">
                        {issues.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </div>
            </CardHeader>
            <CardContent>
              <TabsContent value="items" className="mt-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>SKU</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Unit Price</TableHead>
                        <TableHead>Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.sku}</TableCell>
                          <TableCell>{item.itemName}</TableCell>
                          <TableCell>
                            <div className="text-xs text-gray-500">{item.description}</div>
                          </TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>₹{item.unitPrice.toLocaleString()}</TableCell>
                          <TableCell>₹{item.totalPrice.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
              <TabsContent value="validation" className="mt-0">
                {issues.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-success mx-auto mb-3" />
                    <p className="text-lg font-medium text-gray-900">All items validated successfully</p>
                    <p className="text-sm text-gray-500">No validation issues were found with this purchase order</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {issues.map((issue) => (
                      <div key={issue.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <Badge variant={getSeverityBadgeVariant(issue.severity)} className="mr-2">
                                {issue.severity}
                              </Badge>
                              <span className="font-medium text-gray-900">{issue.field}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{issue.issue}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>
    </main>
  );
};

export default PODetail;